import React, { useEffect, useState } from "react";

import { PageWrapper } from "components/ui/PageWrapper";
import { ComponentLoader } from "components/Loader/ComponentLoader";
import { ErrorComponent } from "components/Alerts/Error";
import { GetAllNavLabels, UpdateNavLabel } from "rest/navLabels";

// Groups the flat nav_labels list back into the same nested shape the
// header menu uses (category -> menu item -> optional sub-menu item), so
// the admin sees the same structure as the live site instead of a flat list.
function groupByNavKey(labels) {
  const categories = {};

  labels.forEach((item) => {
    const parts = item.nav_key.split(":");
    const catId = parts[1];

    if (!categories[catId]) {
      categories[catId] = { key: `cat:${catId}`, label: "", menu: {} };
    }

    if (parts.length === 2) {
      // cat:{id}
      categories[catId].label = item.label;
      categories[catId].navKey = item.nav_key;
    } else if (parts.length === 4) {
      // cat:{id}:menu:{id}
      const menuId = parts[3];
      categories[catId].menu[menuId] = categories[catId].menu[menuId] || {
        navKey: item.nav_key,
        label: item.label,
        sub: [],
      };
      categories[catId].menu[menuId].navKey = item.nav_key;
      categories[catId].menu[menuId].label = item.label;
    } else if (parts.length === 6) {
      // cat:{id}:menu:{id}:sub:{id}
      const menuId = parts[3];
      categories[catId].menu[menuId] = categories[catId].menu[menuId] || {
        navKey: `cat:${catId}:menu:${menuId}`,
        label: "",
        sub: [],
      };
      categories[catId].menu[menuId].sub.push({
        navKey: item.nav_key,
        label: item.label,
      });
    }
  });

  return Object.values(categories)
    .sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true }))
    .map((cat) => ({
      ...cat,
      menu: Object.values(cat.menu),
    }));
}

const EditableLabel = ({ navKey, label, onSave, isSaving }) => {
  const [value, setValue] = useState(label);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setValue(label);
  }, [label]);

  const handleSave = () => {
    if (!value.trim() || value === label) {
      setEditing(false);
      return;
    }
    onSave(navKey, value.trim());
    setEditing(false);
  };

  return (
    <div className="d-flex align-items-center gap-2 mb-2">
      <input
        type="text"
        className="form-control"
        value={value}
        onFocus={() => setEditing(true)}
        onChange={(e) => setValue(e.target.value)}
        style={{ flex: 1 }}
      />
      {editing && (
        <>
          <button
            type="button"
            className="btn btn-sm text-white"
            disabled={isSaving}
            onClick={handleSave}
            style={{ backgroundColor: "#2a3071", borderColor: "#2a3071" }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#34caff";
              e.target.style.borderColor = "#34caff";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#2a3071";
              e.target.style.borderColor = "#2a3071";
            }}
          >
            Save
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setValue(label);
              setEditing(false);
            }}
          >
            Cancel
          </button>
        </>
      )}
    </div>
  );
};

export default function NavLabels() {
  const getAll = GetAllNavLabels();
  const updateLabel = UpdateNavLabel();

  const labels = getAll?.data?.data || [];
  const categories = groupByNavKey(labels);
  const [activeTab, setActiveTab] = useState(0);

  const handleSave = (navKey, label) => {
    updateLabel.mutate({ navKey, label });
  };

  const activeCategory = categories[activeTab];

  return (
    <div className="nav-labels-page">
      <PageWrapper slug="nav-labels" name="Navigation Labels" />

      {getAll?.isError && (
        <ErrorComponent message="OOPS ! something went wrong please try again later" />
      )}

      {getAll?.isPending ? (
        <ComponentLoader />
      ) : (
        <div className="home_banner_input mt-4">
          <p className="text-muted mb-4">
            Rename the text shown for each item in the site's top navigation
            menu. Links and menu structure cannot be changed here.
          </p>

          <ul className="nav nav-tabs mb-4">
            {categories.map((cat, i) => (
              <li className="nav-item" key={cat.key}>
                <button
                  type="button"
                  className={`nav-link ${i === activeTab ? "active" : ""}`}
                  onClick={() => setActiveTab(i)}
                >
                  {cat.label || cat.key}
                </button>
              </li>
            ))}
          </ul>

          {activeCategory && (
            <div className="card">
              <div className="card-body">
                <label className="form-label fw-bold">Category Name</label>
                <EditableLabel
                  navKey={activeCategory.navKey}
                  label={activeCategory.label}
                  onSave={handleSave}
                  isSaving={updateLabel?.isPending}
                />

                {activeCategory.menu.length > 0 && (
                  <div className="mt-3">
                    <label className="form-label fw-bold">Menu Items</label>
                    {activeCategory.menu.map((item) => (
                      <div key={item.navKey} className="mb-3">
                        <EditableLabel
                          navKey={item.navKey}
                          label={item.label}
                          onSave={handleSave}
                          isSaving={updateLabel?.isPending}
                        />
                        {item.sub.length > 0 && (
                          <div className="ms-4">
                            {item.sub.map((sub) => (
                              <EditableLabel
                                key={sub.navKey}
                                navKey={sub.navKey}
                                label={sub.label}
                                onSave={handleSave}
                                isSaving={updateLabel?.isPending}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
